import { useCallback, useEffect, useRef, useState } from "react";
import Dropdown from "./FormElements/DropDown";
import { useTranslation } from "react-i18next";
import { useLocale } from "../context/useLocale";
import useLanguageResource from "../resources/useLanguagesResource";

interface LanguageOption {
  /**
   * Display label for the language (e.g., 'English')
   */
  label: string;
  /**
   * Locale code for the language (e.g., 'en', 'hi')
   */
  value: string;
  /**
   * URL to the flag icon representing the language
   */
  icon: string;
}

// Default English locale fallback
const defaultEnglishLocale: LanguageOption = {
  label: "English",
  value: "en",
  icon: "https://flagcdn.com/16x12/us.png",
};

const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const [languageOption, setLanguageOption] = useState<LanguageOption[]>([]);
  const { selectedLocale, setSelectedLocale } = useLocale();
  const { getLanguages } = useLanguageResource();
  const hasFetchedLanguages = useRef(false);

  /**
   * Fetches available languages and sets the default selected language based on user or browser preference.
   */
  const fetchLanguages = useCallback(async () => {
    try {
      const res = await getLanguages();
      const lan: LanguageOption[] = res.map(data => ({
        label: data.name,
        value: data.locale,
        icon: `https://flagcdn.com/16x12/${data.country_code}.png`,
      }));
      setLanguageOption(lan);
      const selectedLang: string = navigator.language.split("-")[0];
      setSelectedLocale(
        lan.find(item => item.value.toLowerCase() === selectedLang) ||
          defaultEnglishLocale
      );
      i18n.changeLanguage(selectedLang);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [setLanguageOption, setSelectedLocale, i18n, getLanguages]);

  useEffect(() => {
    if (!hasFetchedLanguages.current) {
      fetchLanguages();
      hasFetchedLanguages.current = true;
    }
  }, [fetchLanguages]);

  /**
   * Handles language selection from the dropdown and updates user preference if logged in.
   * @param e The selected language option
   */
  const handleLanguageSelection = async (e: LanguageOption) => {
    const lang = e.value;
    setSelectedLocale(e);
    i18n.changeLanguage(lang);
  };

  return (
    <div className="px-3 mt-2 py-2 z-50 w-full max-w-[340px] flex justify-center ">
      <Dropdown
        options={languageOption}
        placeholder={t("selectLanguage")}
        value={selectedLocale}
        changeHandler={handleLanguageSelection}
      />
    </div>
  );
};

export default LanguageSelector;
